<template>
  <table class="directory-table">
    <thead>
      <!-- Status Indicator -->
      <th
        v-if="!isDeletedTab"
        class="directory-table__heading directory-table__heading--condensed no-wrap"
      >
        Output Status
      </th>

      <th
        v-if="isDeletedTab"
        class="directory-table__heading"
      >
        Permanent <span style="white-space:nowrap">Deletion In</span>
      </th>

      <th class="directory-table__heading">Title</th>

      <th v-if="hasTeams" class="directory-table__heading">Team</th>

      <th class="directory-table__heading tgbNoWrap">Modified</th>

      <th v-if="!hasTeams" class="directory-table__heading">Created</th>

      <th
        v-if="!isDeletedTab"
        class="directory-table__heading"
      >
        <span class="tgb-sr-only">Starred</span>
      </th>

      <!-- Context Menu -->
      <th class="directory-table__heading tgbNoWrap"></th>
    </thead>

    <tbody>
      <tr
        v-for="production in productions"
        :key="production._id"
        class="directory-table__row"
        :data-test-id="production._id"
        data-entity="producer-dashboard-clickProductionRow"
        @click="$emit('dir-item-clicked', production)"
        @dblclick="$emit('dir-item-dblclicked', production)"
        @contextmenu.prevent="$emit('on-dir-context-menu', { e: $event, payload: production })"
        :class="{ 'directory-table__row--active': activeItem === production._id }"
      >
        <!-- Days until perma-delete -->
        <td
          v-if="isDeletedTab"
          data-test="producer-dashboard-table-cell-perma-delete"
          class="directory-table__cell"
        >
          <span>{{ getDaysUntilDelete(production.deleted) }}</span>
        </td>

        <!-- Output Status Indicator -->
        <td
          v-if="!isDeletedTab"
          data-test="producer-dashboard-table-cell-output-status"
          class="directory-table__cell directory-table__cell--condensed text-center"
          :title="production.connected ? 'Active' : 'Inactive'"
        >
          <div
            data-test="producer-dashboard-table-cell-output-status-indicator"
            class="directory-table__cell-status-indicator"
            :class="`directory-table__cell-status-indicator--${production.connected ? 'connected' : 'disconnected'}`"
          />
        </td>

        <!-- Production Name -->
        <td
          class="directory-table__cell"
          data-test="producer-dashboard-table-cell-title"
        >
          <div class="flex items-center">
            <div
              class="tgb-text-bold margin-right-sm directory-table__cell-title"
              :title="production.title"
            >
              {{ production.title }}
            </div>

            <!-- Users in the production -->
            <tgb-avatar-list
              v-if="!isDeletedTab"
              :users="getUsersInProduction(production)"
              :max="2"
            >
              <!-- Blank when no users -->
              <template #noUsers>&nbsp;</template>
            </tgb-avatar-list>
          </div>
        </td>

        <!-- Owner -->
        <td
          v-if="hasTeams"
          class="directory-table__cell directory-table__cell--team"
          data-test="producer-dashboard-table-cell-team"
        >
          <div class="flex items-center">
             <img
              v-if="production.ownedBy && production.ownedBy.profileImageUrl"
              class="tgbUserIconInline"
              :src="production.ownedBy.profileImageUrl"
              :alt="`Profile image of ${production.ownedBy.displayName}`"
              @error="$event.target.src=defaultAvatar"
              :title="production.ownedBy ? `${production.ownedBy.displayName} - ${formatDate(production.created)}` : ''"
            />
          </div>
        </td>

        <!-- Last Updated By -->
        <td
          class="directory-table__cell directory-table__cell--modified-by"
          data-test="producer-dashboard-table-cell-modified-by"
        >
          <div class="flex items-center">
            <div class="production-detail production-detail--group">
              <div>
                {{
                  production.modifiedBy && production.modifiedBy.displayName
                    ? production.modifiedBy.displayName
                    : "N/A"
                }}
              </div>

              <div>
                {{ production.modified | timestamp }}
              </div>
            </div>
          </div>
        </td>

        <!-- Created By (only shown when no team) -->
        <td
          v-if="!hasTeams" class="directory-table__cell directory-table__cell--created-by"
          data-test="producer-dashboard-table-cell-created-by"
        >
          <div class="flex items-center">
             <img
              v-if="production.createdBy && production.createdBy.profileImageUrl"
              class="tgbUserIconInline"
              :src="production.createdBy.profileImageUrl"
              :alt="`Profile image of ${production.createdBy.displayName}`"
              :title="production.createdBy ? `${production.createdBy.displayName} - ${formatDate(production.created)}` : ''"
              @error="$event.target.src=defaultAvatar"
            />
          </div>
        </td>

        <!-- Starred -->
        <td
          v-if="!isDeletedTab"
          class="directory-table__cell directory-table__cell--condensed"
          data-test="producer-dashboard-table-cell-star"
        >
          <label
            @click.prevent.stop="$emit('dir-item-starred', production)"
            class="margin-right-sm cursor-pointer"
          >
            <span class="tgb-sr-only">Star {{ production.title }}</span>

            <input
              type="checkbox"
              class="tgb-sr-only"
              :checked="production.flagged"
            />

            <span
              class="fa-star"
              :class="production.flagged ? 'fas' : 'far'"
            />
          </label>
        </td>


        <!-- Context Menu -->
        <td
          class="directory-table__cell directory-table__cell--condensed"
          data-test="producer-dashboard-table-cell-context-menu"
        >
          <dropdown position="bottom right" :classes="dropdownClasses">
            <template #trigger>
              <div
                class="tgb-icon-ellipsis-wrapper"
                data-entity="producer-dashboard-productionContext"
              >
                <i class="fas fa-ellipsis-h tgb-icon-ellipsis"></i>
              </div>
            </template>

            <template #dropdown-body>
              <tgb-context-menu :dropDownMenu="true" :actions="menuItems">
                <template v-slot="action">
                  <button
                    class="tgbContextMenuButton"
                    :data-entity="`producer-dashboard-${action.key}`"
                    @click="dropDownMenuItemClicked(action.callbackName, action.trigger, production)"
                  >
                    <span v-if="action.icon" class="fas" :class="action.icon"></span>
                    {{ action.text }}
                  </button>
                </template>
              </tgb-context-menu>
            </template>
          </dropdown>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
import { mapGetters } from 'vuex';
import { format, formatDistance, addDays } from 'date-fns';
import { DefaultOptions, DeletedOptions } from './constants';

import TgbAvatarList from '../user-avatar/TgbAvatarList.vue';
import TgbContextMenu from '../context-menu/TgbContextMenu.vue';

export default {
  name: 'DirectoryTable',

  components: {
    TgbAvatarList,
    TgbContextMenu,
  },

  props: {
    items: Array,
    activeItem: [Number, String],
    defaultAvatar: {
      type: String,
      default: './producer/assets/img/profile_placeholder.png',
    },
  },

  computed: {
    ...mapGetters(['hasTeams']),

    productions() {
      return this.items.map((item) => ({
        ...item,
        connected: this.isConnected(item),
      }));
    },

    isDeletedTab() {
      return this.$route.params?.tab === 'deleted';
    },

    menuItems() {
      return this.isDeletedTab ? DeletedOptions : DefaultOptions;
    },
  },

  data() {
    return {
      dropdownClasses: 'dropdown-menu-w-a dropdown-menu-p-0',
    };
  },

  methods: {
    formatDate(ts) {
      return ts ? format(ts, 'MM/dd/yyyy') : 'N/A';
    },

    /**
    * Get list of users in the given production by ID.
    */
    getUsersInProduction(production) {
      const { byProduction } = this.$store.state.clients;
      const users = byProduction[production._id] || [];
      return users;
    },

    /**
    * Check all screens connected to the given production.
    * If any screen is currently open, then return true.
    * Otherwise return false.
    * @param {Object} production
    * @returns {Boolean}
    */
    isConnected(production) {
      // `screenMap` only contains connected screens,
      // so we just need to check length by production ID
      const { screenMap } = this.$store.state.screens;
      const screens = screenMap[production._id] || [];
      return screens.length;
    },

    getDaysUntilDelete(deleted) {
      const now = new Date();
      return deleted ? formatDistance(now, addDays(deleted, 30)) : '30 days';
    },

    dropDownMenuItemClicked(callbackName, trigger, item) {
      this.$emit('dropdown-menu-item-clicked', { callbackName, trigger, item});
    },
  },
};
</script>

<style lang="scss" scoped>
.directory-table {
  background-color: auto;
  border-spacing: 0px 1rem;
  border-collapse: separate;
  width: 100%;

  &__heading {
    font-family: "Barlow", sans-serif;
    font-style: normal;
    font-size: 14px;
    font-weight: 500;
    line-height: 150%;
    padding: 0 0.5rem;
    color: #808689;

    &--condensed {
      /* A little more breathing room around tight columns */
      padding: 0 1rem;
    }
  }

  &__row {
    background-color: var(
      --tgb-surface-default-color
    );

    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;

    &:hover {
      background-color: var(--tgb-directory-card-hover);
      color: #23cdf4;
      cursor: pointer;
    }

    &--active {
      background-color: var(--tgb-directory-card-hover);
      color: #23cdf4;

      .directory-table__cell:first-child {
        border-left: 1px solid #23cdf4;
      }

      .directory-table__cell:last-child {
        border-right: 1px solid #23cdf4;
      }

      .directory-table__cell {
        border-top: 1px solid #23cdf4;
        border-bottom: 1px solid #23cdf4;
      }
    }
  }

  &__cell {
    padding: 1.5rem 0.5rem;

    //border to prevent shifting on active state border add
    border-top: 1px solid transparent;
    border-bottom: 1px solid transparent;

    &--condensed {
      width: 0;
    }

    &:first-child {
      padding: 1rem;
      border-top-left-radius: var(--tgb-border-radius-default);
      border-bottom-left-radius: var(--tgb-border-radius-default);
      //border to prevent shifting on active state border add
      border-left: 1px solid transparent;
    }

    &:last-child {
      padding-right: 1.25rem;
      border-top-right-radius: var(--tgb-border-radius-default);
      border-bottom-right-radius: var(--tgb-border-radius-default);
      //border to prevent shifting on active state border add
      border-right: 1px solid transparent;
    }

    &--team {
      width: 50px;
      max-width: 50px;
    }

    &--modified-by {
      width: 200px;
      max-width: 200px;
    }

    &--created-by {
      width: 200px;
      max-width: 200px;
    }
  }

  &__cell-status-indicator {
    width: 8px;
    height: 8px;
    border-radius: 100%;
    background-color: #00D483;
    margin: auto;

    &--disconnected {
      background-color: #e65447;
    }
  }

  &__cell-title {
    max-width: 33vw;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

.production-detail--group {
  :last-child {
    font-size: 11px;
  }
}

.fa-star {
  /* Small tweak to align things better with ellipsis icon */
  margin-bottom: 4px;
}

.fas.fa-star {
  color: #23CDF4;
}
</style>
